import axios from "axios";
import { ENGAGEMENT_TYPES, CHANNEL_TYPE } from "vue-app/types/enums";
import { isEmpty, sortBy } from "lodash";
import { isMessageValid } from "./helpers/messageValidator";
import { UNLAYER_MESSAGE } from "./helpers/editorDesignConst";
import { serializeDesign } from "./helpers/messageEncoding";
import { withEdroneFooter } from "./helpers/edroneFooter";
import { mapEditorConfig } from "./helpers/configMapper";
import { replaceCategories } from "./helpers/messageCategoriesFixer";
import { useGlobalLoader } from "vue-app/composables/useGlobalLoader";

const { hideGlobalLoader } = useGlobalLoader();

import router from "../../../../../../routes";

const multiChannelEngagements = [
  ENGAGEMENT_TYPES.ABANDONED_CARTS,
  ENGAGEMENT_TYPES.NEWSLETTER_SUBSCRIPTION,
  ENGAGEMENT_TYPES.BIRTHDAY,
];

export default {
  setDefaultState({ commit }) {
    commit("setDefaultState");
  },
  setMessageData({ commit }, messageData) {
    commit("setMessageData", messageData);
  },
  fetchInitialState({ commit, rootGetters, dispatch }, { engagementName }) {
    axios
      .get(`/json/${engagementName}/design/parameters`)
      .then(({ data: cfg }) => {
        if (cfg.errors && cfg.errors.redirectUrl) {
          router.push({ path: cfg.errors.redirectUrl });
          return;
        }

        window.engagementMessageId = cfg.engagementMessage != null ? cfg.engagementMessage.engagementMessageId : null;

        const config = mapEditorConfig({
          cfg,
          lang: rootGetters["global/lang"],
          isDev: rootGetters["global/isDev"],
          isDemo: rootGetters["global/isDemo"],
          canModifyUTM: Boolean(rootGetters["global/trackerConfiguration"].canModifyUtm),
        });

        commit("initialState", config);
        commit("editorReady", true);
        hideGlobalLoader();
      })
      .catch(() => {
        window.window.showToast("Cannot load editor - please try again in a while", "error");
      });
  },
  findVal(object, key) {
    let value;
    Object.keys(object).some(function (k) {
      if (k === key) {
        value = object[k];
        return true;
      }
      if (object[k] && typeof object[k] === "object") {
        value = this.findVal(object[k], key);
        return value !== undefined;
      }
    });
    return value;
  },
  saveMessageSMS({ state, commit }, options = {}) {
    const requestData = {
      engagement_type_id: state.engagementTypeId,
      html: state.message.html,
      subject: state.message.subject,
      preheader: state.message.preheader,
      autosave: false,
    };

    if (state.orderIndex != null) {
      requestData.order_index = state.orderIndex;
    }

    if (state.groupId != null) {
      requestData.group_id = state.groupId;
    }

    if (state.isMultiLanguage && state.messageLanguage != null) {
      requestData.engagement_message_language = state.messageLanguage;
    }

    if (multiChannelEngagements.includes(state.engagementTypeId)) {
      requestData.channel_type_id = state.editorType;
    }

    commit("isLoading", true);
    axios
      .post("/engagements/design/saveMessage", requestData)
      .then(() => {
        if (options.showSuccessMessage) {
          window.showToast($_t("Your message has been saved"), "success");
        }
      })
      .catch(() => {
        window.showToast($_t("Unable to save your message - please try again"), "error");
      })
      .finally(() => {
        commit("isLoading", false);
      });
    commit("isLoading", true);
    axios
      .post("/engagements/design/saveMessage", requestData)
      .then(() => {
        window.showToast($_t("Your message has been saved"), "success");
      })
      .catch(() => {
        window.showToast($_t("Unable to save your message - please try again"), "error");
      })
      .finally(() => {
        commit("isLoading", false);
      });
  },
  saveMessage({ state, commit }, options = {}) {
    if (!isMessageValid(state)) {
      return false;
    }

    if (window.unlayer !== null) {
      window.unlayer.exportHtml(({ html, design }) => {
        commit("messageContent", { html, design });

        const htmlWithFooter = withEdroneFooter(html, design);

        let config = JSON.parse(JSON.stringify(design));
        config = replaceCategories(config, "oneProduct", []);
        config = replaceCategories(config, "twoProducts", []);
        config = replaceCategories(config, "threeProducts", []);
        config = replaceCategories(config, "fourProducts", []);
        config = replaceCategories(config, "fiveProducts", []);
        config = replaceCategories(config, "sixProducts", []);
        const requestData = {
          engagement_type_id: state.engagementTypeId,
          html: htmlWithFooter,
          editor_layout: !isEmpty(config) ? serializeDesign(config) : null,
          subject: state.message.subject,
          preheader: state.message.preheader,
          autosave: false,
        };

        if (state.orderIndex != null) {
          requestData.order_index = state.orderIndex;
        }

        if (state.groupId != null) {
          requestData.group_id = state.groupId;
        }

        if (state.isMultiLanguage && state.messageLanguage != null) {
          requestData.engagement_message_language = state.messageLanguage;
        }

        if (multiChannelEngagements.includes(state.engagementTypeId)) {
          requestData.channel_type_id = state.editorType;
        }

        commit("isLoading", true);
        axios
          .post("/engagements/design/saveMessage", requestData)
          .then(() => {
            if (options.showSuccessMessage) {
              window.showToast($_t("Your message has been saved"), "success");
            }
          })
          .catch(() => {
            window.showToast($_t("Unable to save your message - please try again"), "error");
          })
          .finally(() => {
            commit("isLoading", false);
          });
      });
    } else {
      window.showToast($_t("Wait for the editor to load"), "warn");
    }
  },
  loadMessage({ state, commit }) {
    const requestData = {
      engagement_type_id: state.engagementTypeId,
    };

    if (state.orderIndex != null) {
      requestData.order_index = state.orderIndex;
    }

    if (state.groupId != null) {
      requestData.group_id = state.groupId;
    }

    if (multiChannelEngagements.includes(state.engagementTypeId) && state.messageChannelType != null) {
      requestData.send_channel = state.messageChannelType;
    }

    if (state.isMultiLanguage && state.messageLanguage) {
      requestData.engagement_message_language = state.messageLanguage;
    }

    commit("isNewMessageLoading", true);
    axios
      .post("/engagements/design/loadMessage", requestData)
      .then(({ data }) => {
        commit("applyLoadedMessage", data);
        commit("messageHistory", []);
      })
      .catch(() => {
        window.showToast("Please try again", "error");
      })
      .finally(() => {
        commit("isNewMessageLoading", false);
      });
  },
  updateMessageSMSContent({ commit, dispatch }, { html, design }) {
    commit("messageContent", { html, design });
    dispatch("saveMessageSMS");
  },
  updateMessageContent({ commit, dispatch }, { html, design }) {
    commit("messageContent", { html, design });
    dispatch("storeMessageHistory");
    dispatch("saveMessage");
  },
  updateSubject({ commit, dispatch }, subject) {
    commit("messageSubject", subject);
    dispatch("storeMessageHistory");
    dispatch("saveMessage");
  },
  updatePreheader({ commit, dispatch }, preheader) {
    commit("messagePreheader", preheader);
    dispatch("storeMessageHistory");
    dispatch("saveMessage");
  },
  updateCouponPoolId({ commit, dispatch }, couponPoolId) {
    commit("couponPoolId", couponPoolId);
  },
  clearMessage({ state, commit, dispatch }) {
    dispatch("storeMessageHistory");
    commit("message", { subject: state.message.subject, html: "", design: {} });
  },
  copyMessage({ state, commit }) {
    window.localStorage.setItem(UNLAYER_MESSAGE, JSON.stringify(state.message));
    commit("copyMessageToClipboard");
    window.showToast(window._trans.designCopied, "success");
  },
  async pasteMessage({ commit, dispatch }) {
    dispatch("storeMessageHistory");
    try {
      const clipboard = await navigator.clipboard.readText();
      const parsedClipboard = JSON.parse(clipboard);
      if (parsedClipboard?.design) {
        commit("pasteMessageFromClipboard", parsedClipboard);
      } else {
        commit("pasteMessageFromLocalStorage");
      }
    } catch (error) {
      commit("pasteMessageFromLocalStorage");
    }
    setTimeout(() => {
      dispatch("saveMessage");
      window.showToast($_t("Success"), "success");
    }, 1);
  },
  applyMessageProperties({ dispatch, commit }, messageProperties) {
    commit("messageProperties", messageProperties);
    dispatch("loadMessage");
  },
  updateUTMs({ state }) {
    const utmData = new FormData();

    utmData.append("utm_campaign", state.utm.campaign);
    utmData.append("utm_source", state.utm.source);
    utmData.append("utm_medium", state.utm.medium);
    utmData.append("utm_term", state.utm.term);
    utmData.append(
      "are_overwritten",
      (state.utm.isUTMModificationAllowed ? state.utm.areOverwritten : true).toString()
    );
    utmData.append("engagement_type_id", state.engagementTypeId.toString());
    utmData.append("engagement_message_id", state.engagementMessageId.toString());

    axios
      .post(state.utm.urlToUpdate, utmData)
      .then(() => {
        window.showToast("Links has been updated", "success");
      })
      .catch(() => {
        window.showToast("Please try again", "error");
      });
  },
  storeMessageHistory({ state, commit }) {
    if (state.editorType !== CHANNEL_TYPE.EMAIL) {
      return;
    }

    const MAX_LEN = 10;
    const historyEntry = { ...state.message };

    let updatedHistory = [];
    if (state.messageHistory.length < MAX_LEN) {
      updatedHistory = [...state.messageHistory, historyEntry];
    } else {
      updatedHistory = [state.messageHistory.slice(1), historyEntry];
    }
    commit("messageHistory", updatedHistory);
  },
  toggleTemplates({ state, commit }) {
    commit("templatesVisibility", !state.isTemplatesSectionVisible);
    if (state.templates.length > 0 || state.templatesLoading) {
      return false;
    }

    commit("templatesLoading", true);

    axios
      .get("https://api.unlayer.com/v2/templates", {
        headers: {
          Authorization: `Basic ${window.btoa("F1hNMRdLfCRBG5vN55ugvSqTZPHA8TrYzEJTEWrGJyZOrr9u9vWY93Ye8nFx8ELq:")}`,
        },
      })
      .then(({ data: { data: templates } }) => {
        const emailTemplates = templates.map((template) => ({
          templateThumbnailUrl: `https://api.unlayer.com/v2/editor/10391/templates/${template.id}/thumbnail`,
          ...template,
        }));
        commit(
          "templates",
          sortBy(emailTemplates, (t) => t.name)
        );
      })
      .finally(() => {
        commit("templatesLoading", false);
      });
  },
  setEditor({ commit }, editor) {
    commit("setEditor", editor);
  },
  applyTemplate({ state, commit, dispatch }, templateId) {
    const template = state.templates.find((t) => t.id === templateId);
    dispatch("storeMessageHistory");
    commit("message", {
      subject: state.message.subject,
      design: template.design,
      html: "",
    });
    window.showToast($_t("Click save to use this template"), "info");
  },
  applyHistory({ state, commit }, design) {
    commit("message", {
      subject: state.message.subject,
      design: design,
      html: "",
    });
    window.showToast($_t("Click save to use this message"), "info");
  },
};
